import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { cx } from "styled-system/css";
import { ak } from "./admin-styles";
import { Banner, PageHead, Section, Status } from "~/components/editorial";
import { api } from "~/lib/api";

interface SlackConfig {
  enabled: boolean;
  channelId: string;
  dayOfWeek: number;
  hourUtc: number;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, "0")}:00 UTC`,
}));

const DEFAULT_CONFIG: SlackConfig = {
  enabled: false,
  channelId: "",
  dayOfWeek: 1,
  hourUtc: 13,
};

export function SlackSettingsPage() {
  const { data: savedConfig, isLoading: loading } = useQuery({
    queryKey: ["slack-config"],
    // Not configured yet - the form falls back to defaults
    queryFn: async () =>
      (await api.get<{ data: SlackConfig | null }>("/briefing/slack-config"))
        .data,
    staleTime: 0,
    retry: false,
  });
  // Edits live here until saved; untouched, the form shows the saved config
  const [draft, setConfig] = useState<SlackConfig | null>(null);
  const config = draft ?? savedConfig ?? DEFAULT_CONFIG;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savedHere, setConfigured] = useState(false);
  const configured = savedHere || !!savedConfig;

  const [testing, setTesting] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put("/briefing/slack-config", config);
      setSuccess("Slack digest saved.");
      setConfigured(true);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to save configuration",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      await api.post("/briefing/slack-test");
      setSuccess("Test message sent to Slack.");
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send test message",
      );
    } finally {
      setTesting(false);
    }
  };

  const dayLabel =
    DAYS_OF_WEEK.find((d) => d.value === config.dayOfWeek)?.label ?? "";
  const hourLabel = `${config.hourUtc.toString().padStart(2, "0")}:00 UTC`;

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin · Slack"
        title="Slack digest"
        dek="A weekly engineering briefing, posted to one channel on a fixed schedule."
      />

      {success && <Banner>{success}</Banner>}

      {error && (
        <Banner tone="caution" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Section
        kicker="Delivery"
        title="Where and when it posts"
        actions={
          loading ? null : configured && config.enabled ? (
            <Status kind="ok">
              On · {dayLabel}s <span className={ak.mono}>{hourLabel}</span>
            </Status>
          ) : configured ? (
            <Status kind="off">Saved, paused</Status>
          ) : (
            <Status kind="off">Not configured</Status>
          )
        }
      >
        {loading ? (
          <div className={ak.loading}>Loading…</div>
        ) : (
          <form onSubmit={handleSave}>
            <div className={ak.field}>
              <label className={ak.checkRow}>
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) =>
                    setConfig({ ...config, enabled: e.target.checked })
                  }
                />
                Send the weekly digest
              </label>
            </div>

            <div className={ak.field}>
              <label className={ak.label} htmlFor="slack-channel">
                Slack channel
              </label>
              <input
                id="slack-channel"
                type="text"
                value={config.channelId}
                onChange={(e) =>
                  setConfig({ ...config, channelId: e.target.value })
                }
                placeholder="#engineering-leads"
                className={cx(ak.input, ak.mono)}
              />
              <p className={ak.hint}>
                Channel name or ID. Needs{" "}
                <code className={ak.mono}>SLACK_BOT_TOKEN</code> set in the
                server environment.
              </p>
            </div>

            <div className={ak.formRow}>
              <div className={ak.field}>
                <label className={ak.label} htmlFor="slack-day">
                  Day of week
                </label>
                <select
                  id="slack-day"
                  value={config.dayOfWeek}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      dayOfWeek: parseInt(e.target.value),
                    })
                  }
                  className={ak.select}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={ak.field}>
                <label className={ak.label} htmlFor="slack-hour">
                  Time (UTC)
                </label>
                <select
                  id="slack-hour"
                  value={config.hourUtc}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      hourUtc: parseInt(e.target.value),
                    })
                  }
                  className={cx(ak.select, ak.mono)}
                >
                  {HOURS.map((hour) => (
                    <option key={hour.value} value={hour.value}>
                      {hour.label}
                    </option>
                  ))}
                </select>
                <p className={ak.hint}>
                  <span className={ak.mono}>13:00</span> UTC is{" "}
                  <span className={ak.mono}>9:00</span> Eastern (daylight time).
                </p>
              </div>
            </div>

            <div className={ak.formActions}>
              <button type="submit" disabled={saving} className={ak.btn}>
                {saving ? "Saving…" : "Save configuration"}
              </button>
              {configured && (
                <button
                  type="button"
                  disabled={testing || !config.channelId}
                  className={ak.btnSecondary}
                  onClick={handleTest}
                >
                  {testing ? "Sending…" : "Send a test message"}
                </button>
              )}
            </div>
          </form>
        )}
      </Section>
    </div>
  );
}
